// Notification + audit-log store.
//
// Per the agent-ui skill we avoid third-party toast libraries — this is a
// tiny in-house queue. Toasts auto-dismiss; the audit log keeps a running
// transcript of meaningful state transitions for debugging.

import { create } from 'zustand';
import type { SessionId } from '@prakash-software/shared';
import { fireBrowserNotification, onLandingRoute, tabInactive } from '../lib/pwa';

export type ToastKind = 'visitor' | 'chat' | 'escalate' | 'disconnect' | 'system';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  /** When set, clicking the toast opens this conversation. */
  sessionId?: SessionId;
  /** ms until auto-dismiss; default 5000 */
  ttl?: number;
  at: number;
}

export interface AuditEntry {
  id: string;
  at: number;
  /** Free-form text describing what happened. */
  text: string;
}

export interface NotificationPrefs {
  toastsEnabled: boolean;
  soundEnabled: boolean;
  perKind: Record<ToastKind, boolean>;
}

interface NotificationsState {
  toasts: Toast[];
  audit: AuditEntry[];
  prefs: NotificationPrefs;
  push: (t: Omit<Toast, 'id' | 'at'> & { id?: string }) => void;
  dismiss: (id: string) => void;
  setPrefs: (patch: Partial<NotificationPrefs>) => void;
  log: (text: string) => void;
}

const PREFS_KEY = 'prakashsoftware.agent.notifications';

const DEFAULT_PREFS: NotificationPrefs = {
  toastsEnabled: true,
  soundEnabled: true,
  perKind: {
    visitor: true,
    chat: true,
    escalate: true,
    disconnect: true,
    system: true,
  },
};

function loadPrefs(): NotificationPrefs {
  try {
    const raw = sessionStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: NotificationPrefs) {
  try {
    sessionStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* non-fatal */
  }
}

let counter = 0;
const nextId = () => `n_${Date.now().toString(36)}_${++counter}`;
const AUDIT_CAP = 80;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  toasts: [],
  audit: [],
  prefs: loadPrefs(),
  push: (toast) => {
    const prefs = get().prefs;
    if (!prefs.toastsEnabled) return;
    if (!prefs.perKind[toast.kind]) return;
    // Suppress agent notifications while the marketing landing page is showing
    // — it's a public surface that also embeds the widget, so it shouldn't pop
    // toasts, sounds, or OS notifications at the signed-in agent.
    if (onLandingRoute()) return;
    const id = toast.id ?? nextId();
    const next: Toast = { ...toast, id, at: Date.now() };
    set((s) => ({ toasts: [...s.toasts, next] }));
    // Sound — generated via Web Audio (no asset). Best-effort.
    if (prefs.soundEnabled) chime();
    // Mirror to an OS notification ONLY when the agent isn't looking at the
    // tab — when it's in front the in-app toast is enough. Clicking the OS
    // notification focuses the tab and opens the conversation.
    if (tabInactive()) {
      fireBrowserNotification(toast.title, {
        body: toast.body,
        sessionId: toast.sessionId,
        tag: toast.sessionId,
      });
    }
    // Also append to the audit log.
    get().log(toast.title + (toast.body ? ` · ${toast.body}` : ''));
    // Auto-dismiss after ttl.
    const ttl = toast.ttl ?? 5000;
    window.setTimeout(() => get().dismiss(id), ttl);
  },
  dismiss: (id) =>
    set((s) => ({
      toasts: s.toasts.filter((t) => t.id !== id),
    })),
  setPrefs: (patch) =>
    set((s) => {
      const next = { ...s.prefs, ...patch };
      savePrefs(next);
      return { prefs: next };
    }),
  log: (text) =>
    set((s) => ({
      audit: [{ id: nextId(), at: Date.now(), text }, ...s.audit].slice(0, AUDIT_CAP),
    })),
}));

// ─── Tiny chime via Web Audio ───
let audioCtx: AudioContext | null = null;
function chime() {
  if (typeof window === 'undefined') return;
  try {
    audioCtx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const ctx = audioCtx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.18);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.34);
  } catch {
    /* Audio may not be unlocked yet — fine, we'll get the next one. */
  }
}
